import { AlertTriangle, Ban, Check, ChevronLeft, ChevronRight, CircleDashed, Clock, DoorClosed, Filter, ImageOff, Images, List as ListIcon, RefreshCw, Sparkles, Unlink, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import BONavigation from "components/admin/BONavigation";
import {
    getAdminNewsArticles,
    setArticleStatus,
    setArticleCity,
    setArticleCategory,
    setArticleEstablishment,
    setArticleImage,
    scrapeArticleImages,
    runNewsImageScrape,
    setArticleFranchise,
    triggerNewsFetch,
    NewsItem,
    NewsStatus,
    LinkedEstablishment,
    WeekStats,
    JevEvent,
} from "interfaces/restaurantNews";
import { JEV_EVENT_LABEL, JEV_LEAD_SCORE } from "./jevParts";
import { getFranchises, FranchiseDoc } from "interfaces/establishments";
import { useDebouncedEffect } from "../../../hooks/useDebouncedEffect";
import {
    ArticleRow,
    currentWeekParam,
    formatWeekRange,
    isoWeekNumber,
    shiftWeek,
    FOCUS_RING,
} from "./newsArticleParts";

// ─── Jev quick filters ────────────────────────────────────────────────────────

type JevQuick = "" | "leads" | "review" | "roundups" | "closings" | "unscored";

// Each chip is one triage list. The params mirror the backend's `jevCounts` definitions
// (restaurantNewsController), so a chip's count is exactly what clicking it shows.
const JEV_QUICK_FILTERS: { key: Exclude<JevQuick, "">; label: string; title: string; icon: typeof Sparkles }[] = [
    { key: "leads", label: "Leads probables", title: `Score Jev ≥ ${JEV_LEAD_SCORE} : ouverture d'un seul lieu, bien identifié`, icon: Sparkles },
    { key: "review", label: "À revoir", title: "Jev hésite sur la nature de l'article ou le nombre de lieux", icon: AlertTriangle },
    { key: "roundups", label: "Listes de lieux", title: "Articles sur plusieurs lieux (« 10 nouvelles adresses ») ou un plan d'expansion", icon: ListIcon },
    { key: "closings", label: "Fermetures", title: "Jev lit l'article comme une fermeture", icon: DoorClosed },
    { key: "unscored", label: "Non scorés", title: "Pas encore passés par Jev", icon: CircleDashed },
];

function jevQuickParams(quick: JevQuick) {
    switch (quick) {
        case "leads": return { jevMinScore: JEV_LEAD_SCORE };
        case "review": return { jevReview: "true" as const };
        case "roundups": return { jevScope: "several_venues,group_expansion" };
        case "closings": return { jevEvent: "closing" };
        case "unscored": return { jevScored: "false" as const };
        default: return {};
    }
}

// ─── main BO page ─────────────────────────────────────────────────────────────

type StatusFilter = "all" | "pending" | "approved" | "rejected";

export default function NewsArticles() {
    const [items, setItems] = useState<NewsItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [category, setCategory] = useState("all");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
    const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
    const [pendingCount, setPendingCount] = useState(0);
    const [allFranchises, setAllFranchises] = useState<FranchiseDoc[]>([]);
    const [toast, setToast] = useState<string | null>(null);
    const [franchiseFilter, setFranchiseFilter] = useState<"" | "true" | "false">("");
    const [franceFilter, setFranceFilter] = useState<"" | "true" | "false">("");
    const [hasPhotoFilter, setHasPhotoFilter] = useState<"" | "true" | "false">("");
    const [hasEstablishmentFilter, setHasEstablishmentFilter] = useState<"" | "true" | "false">("");
    const [pickableFilter, setPickableFilter] = useState(false);
    const [weekStart, setWeekStart] = useState<string | null>(() => currentWeekParam());
    const [weekStats, setWeekStats] = useState<WeekStats | null>(null);
    const [missingCounts, setMissingCounts] = useState({ noPhoto: 0, noEstablishment: 0, pickable: 0, unscraped: 0 });
    const [scrapingBatch, setScrapingBatch] = useState(false);
    const [jevQuick, setJevQuick] = useState<JevQuick>("");
    const [jevEvent, setJevEvent] = useState<"" | JevEvent>("");
    const [sortBy, setSortBy] = useState<"published" | "jev">("published");
    const [jevCounts, setJevCounts] = useState({ leads: 0, review: 0, roundups: 0, closings: 0, unscored: 0 });
    const [jevEventCounts, setJevEventCounts] = useState<Partial<Record<JevEvent | "all", number>>>({});
    const LIMIT = 30;

    useEffect(() => { getFranchises().then(setAllFranchises).catch(() => {}); }, []);

    const load = useCallback(async (
        cat: string, status: StatusFilter, pg: number,
        franchise: string, france: string, hasPhoto: string, hasEstablishment: string, week: string | null,
        pickable: boolean, jev: { quick: JevQuick; event: "" | JevEvent; sort: "published" | "jev" }
    ) => {
        setLoading(true);
        try {
            const data = await getAdminNewsArticles({
                ...jevQuickParams(jev.quick),
                // A quick chip that already pins the type ("Fermetures") wins over the select.
                ...(jev.event && jev.quick !== "closings" ? { jevEvent: jev.event } : {}),
                sort: jev.sort,
                category: cat !== "all" ? cat : undefined,
                status: status !== "all" ? status : "all",
                isFranchise: (franchise as "true" | "false") || undefined,
                isInFrance: (france as "true" | "false") || undefined,
                hasPhoto: (hasPhoto as "true" | "false") || undefined,
                hasEstablishment: (hasEstablishment as "true" | "false") || undefined,
                pickable: pickable ? "true" : undefined,
                weekStart: week ?? undefined,
                page: pg,
                limit: LIMIT,
            });
            setItems(data.items);
            setTotal(data.total);
            setTotalPages(data.totalPages);
            setCategoryCounts(data.categoryCounts);
            setPendingCount(data.pendingCount);
            setWeekStats(data.weekStats);
            setMissingCounts(data.missingCounts);
            if (data.jevCounts) setJevCounts(data.jevCounts);
            if (data.jevEventCounts) setJevEventCounts(data.jevEventCounts);
        } finally {
            setLoading(false);
        }
    }, []);

    useDebouncedEffect(() => {
        load(category, statusFilter, page, franchiseFilter, franceFilter, hasPhotoFilter, hasEstablishmentFilter, weekStart, pickableFilter, { quick: jevQuick, event: jevEvent, sort: sortBy });
    }, [category, statusFilter, page, franchiseFilter, franceFilter, hasPhotoFilter, hasEstablishmentFilter, weekStart, pickableFilter, jevQuick, jevEvent, sortBy, load]);

    const reload = useCallback(() => {
        load(category, statusFilter, page, franchiseFilter, franceFilter, hasPhotoFilter, hasEstablishmentFilter, weekStart, pickableFilter, { quick: jevQuick, event: jevEvent, sort: sortBy });
    }, [load, category, statusFilter, page, franchiseFilter, franceFilter, hasPhotoFilter, hasEstablishmentFilter, weekStart, pickableFilter, jevQuick, jevEvent, sortBy]);

    const handleSetStatus = async (id: string, status: NewsStatus) => {
        await setArticleStatus(id, status);
        const wasPending = items.find((a) => a._id === id)?.status === "pending";
        if (statusFilter === "pending" && status !== "pending") {
            setItems((prev) => prev.filter((a) => a._id !== id));
            setTotal((t) => Math.max(0, t - 1));
        } else {
            setItems((prev) => prev.map((a) => a._id === id ? { ...a, status } : a));
        }
        if (wasPending && status !== "pending") setPendingCount((c) => Math.max(0, c - 1));
        else if (!wasPending && status === "pending") setPendingCount((c) => c + 1);
        showToast(status === "approved" ? "Article approuvé" : status === "rejected" ? "Article rejeté" : "Article en attente");
    };

    const handleSetCity = async (id: string, city: string | null) => {
        // City feeds the matching algorithm — the backend recomputes the suggestion, so merge
        // whatever it comes back with instead of only the city field.
        const updated = await setArticleCity(id, city);
        setItems((prev) => prev.map((a) => a._id === id ? {
            ...a,
            city,
            suggestedEstablishment: updated.suggestedEstablishment,
            suggestedEstablishmentScore: updated.suggestedEstablishmentScore,
        } : a));
    };

    const handleSetCategory = async (id: string, category: "opening" | "closing" | "ownership" | "recruitment") => {
        const updated = await setArticleCategory(id, category);
        setItems((prev) => prev.map((a) => a._id === id ? {
            ...a,
            category,
            suggestedEstablishment: updated.suggestedEstablishment,
            suggestedEstablishmentScore: updated.suggestedEstablishmentScore,
        } : a));
        showToast("Catégorie mise à jour");
    };

    const handleSetImage = async (id: string, image: string | null) => {
        await setArticleImage(id, image);
        setItems((prev) => prev.map((a) => a._id === id ? { ...a, image, imageManuallySet: image !== null } : a));
        showToast(image ? "Image de preview mise à jour" : "Image retirée");
    };

    const handleScrapeImages = async (id: string) => {
        const updated = await scrapeArticleImages(id);
        setItems((prev) => prev.map((a) => a._id === id ? {
            ...a,
            image: updated.image,
            imageManuallySet: updated.imageManuallySet,
            scrapedImages: updated.scrapedImages ?? [],
            imagesScrapedAt: updated.imagesScrapedAt,
            imageScrapeOutcome: updated.imageScrapeOutcome,
            imageScrapeDetail: updated.imageScrapeDetail,
        } : a));
        const n = updated.scrapedImages?.length ?? 0;
        showToast(
            updated.imageScrapeOutcome === "blocked"
                ? `Source bloquée${updated.imageScrapeDetail ? ` (${updated.imageScrapeDetail})` : ""} — aucune image récupérable`
                : updated.imageScrapeOutcome === "error"
                    ? `Page inaccessible${updated.imageScrapeDetail ? ` (${updated.imageScrapeDetail})` : ""}`
                    : `${n} image${n > 1 ? "s" : ""} trouvée${n > 1 ? "s" : ""}`
        );
    };

    const handleScrapeBatch = async () => {
        setScrapingBatch(true);
        try {
            const r = await runNewsImageScrape();
            const blockedPart = r.blocked > 0 ? ` · ${r.blocked} bloqué${r.blocked > 1 ? "s" : ""}` : "";
            showToast(`${r.scraped} scrapé${r.scraped > 1 ? "s" : ""} · ${r.withImages} avec images${blockedPart} · ${r.remaining} restant${r.remaining > 1 ? "s" : ""}`);
            reload();
        } catch {
            showToast("Erreur lors du scraping");
        } finally {
            setScrapingBatch(false);
        }
    };

    const handleSetEstablishment = async (id: string, est: LinkedEstablishment | null) => {
        await setArticleEstablishment(id, est?._id ?? null);
        setItems((prev) => prev.map((a) => a._id === id ? {
            ...a,
            linkedEstablishment: est,
            // clear suggestion when a manual link is made
            suggestedEstablishment: null,
            suggestedEstablishmentScore: null,
        } : a));
        showToast(est ? `Lié à ${est.name}` : "Établissement dissocié");
    };

    const handleSetFranchise = async (id: string, franchiseName: string | null) => {
        const updated = await setArticleFranchise(id, franchiseName);
        setItems((prev) => prev.map((a) => a._id === id ? { ...a, franchise: updated.franchise } : a));
        if (updated.franchise && !allFranchises.find((f) => f._id === updated.franchise!._id)) {
            setAllFranchises((prev) => [...prev, updated.franchise!].sort((a, b) => a.name.localeCompare(b.name, "fr")));
        }
    };

    const handleFetch = async () => {
        setFetching(true);
        try {
            const result = await triggerNewsFetch();
            showToast(`${result.saved} article${result.saved > 1 ? "s" : ""} importé${result.saved > 1 ? "s" : ""}`);
            load(category, statusFilter, 1, franchiseFilter, franceFilter, hasPhotoFilter, hasEstablishmentFilter, weekStart, pickableFilter, { quick: jevQuick, event: jevEvent, sort: sortBy });
            setPage(1);
        } catch {
            showToast("Erreur lors de l'import");
        } finally {
            setFetching(false);
        }
    };

    const showToast = (msg: string) => {
        setToast(msg);
        setTimeout(() => setToast(null), 2500);
    };

    const resetFilters = () => {
        setCategory("all");
        setStatusFilter("all");
        setFranchiseFilter("");
        setFranceFilter("");
        setHasPhotoFilter("");
        setHasEstablishmentFilter("");
        setPickableFilter(false);
        setJevQuick("");
        setJevEvent("");
        setWeekStart(currentWeekParam());
        setPage(1);
    };

    const hasActiveFilters = category !== "all" || statusFilter !== "all" || franchiseFilter !== "" || franceFilter !== "" || hasPhotoFilter !== "" || hasEstablishmentFilter !== "" || pickableFilter || jevQuick !== "" || jevEvent !== "";
    const totalCount = Object.values(categoryCounts).reduce((a, b) => a + b, 0);

    const selectCls = `text-xs border border-border rounded-lg px-2.5 py-1.5 text-ink bg-surface ${FOCUS_RING}`;

    return (
        <div className="min-h-screen bg-canvas">
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-ink text-ink-inverse text-sm px-4 py-3 rounded-2xl shadow-float">
                    <span>{toast}</span>
                    <button onClick={() => setToast(null)} className={`rounded ${FOCUS_RING}`}>
                        <X className="w-4 h-4 text-ink-inverse-muted hover:text-ink-inverse" />
                    </button>
                </div>
            )}

            <div className="mx-auto max-w-5xl px-4 py-6">
                <div className="mb-6">
                    <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">Modération — Actualités</h1>
                    <p className="text-xs text-ink-muted mt-0.5">
                        {totalCount} articles · {pendingCount} en attente
                    </p>
                </div>

                <BONavigation />

                {/* week progress — the intern's weekly backlog at a glance */}
                <div className="mb-4 mt-2 flex items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-3">
                    <button
                        onClick={() => { setWeekStart((w) => shiftWeek(w ?? currentWeekParam(), -1)); setPage(1); }}
                        title="Semaine précédente"
                        className={`flex-shrink-0 p-1.5 text-ink-faint hover:text-ink rounded-md hover:bg-surface-sunken transition-colors ${FOCUS_RING}`}
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>

                    <div className="flex-1 min-w-0">
                        {weekStart ? (
                            <>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-semibold text-ink">
                                        Semaine {isoWeekNumber(weekStart)} · {formatWeekRange(weekStart)}
                                    </span>
                                    {weekStart !== currentWeekParam() && (
                                        <button onClick={() => { setWeekStart(currentWeekParam()); setPage(1); }}
                                            className={`text-[11px] text-accent hover:underline rounded ${FOCUS_RING}`}>
                                            Revenir à cette semaine
                                        </button>
                                    )}
                                    <button onClick={() => { setWeekStart(null); setPage(1); }}
                                        className={`ml-auto text-[11px] text-ink-faint hover:text-ink transition-colors rounded ${FOCUS_RING}`}>
                                        Toutes les semaines
                                    </button>
                                </div>
                                <div className="mt-1.5 h-1.5 w-full rounded-full bg-surface-sunken overflow-hidden">
                                    <div
                                        className="h-full bg-ink rounded-full transition-all duration-300"
                                        style={{
                                            width: `${weekStats && weekStats.total > 0
                                                ? Math.round(((weekStats.total - weekStats.pending) / weekStats.total) * 100)
                                                : 0}%`,
                                        }}
                                    />
                                </div>
                                <p className="mt-1 text-[11px] text-ink-muted">
                                    {weekStats
                                        ? weekStats.total === 0
                                            ? "Aucun article reçu cette semaine"
                                            : `${weekStats.pending} restant${weekStats.pending > 1 ? "s" : ""} sur ${weekStats.total} reçu${weekStats.total > 1 ? "s" : ""}`
                                        : "…"}
                                </p>
                            </>
                        ) : (
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-ink">Toutes les semaines</span>
                                <button onClick={() => { setWeekStart(currentWeekParam()); setPage(1); }}
                                    className={`text-[11px] text-accent hover:underline rounded ${FOCUS_RING}`}>
                                    Revenir à la semaine en cours
                                </button>
                            </div>
                        )}
                    </div>

                    <button
                        onClick={() => { setWeekStart((w) => shiftWeek(w ?? currentWeekParam(), 1)); setPage(1); }}
                        title="Semaine suivante"
                        disabled={weekStart === null}
                        className={`flex-shrink-0 p-1.5 text-ink-faint hover:text-ink rounded-md hover:bg-surface-sunken transition-colors disabled:opacity-30 ${FOCUS_RING}`}
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>

                {/* toolbar */}
                <div className="flex flex-wrap items-center gap-2 mb-4 mt-2">
                    <select
                        value={category}
                        onChange={(e) => { setCategory(e.target.value); setPage(1); }}
                        className={selectCls}
                    >
                        <option value="all">Toutes catégories</option>
                        <option value="opening">Ouvertures ({categoryCounts.opening ?? 0})</option>
                        <option value="closing">Fermetures ({categoryCounts.closing ?? 0})</option>
                        <option value="ownership">Cessions ({categoryCounts.ownership ?? 0})</option>
                        <option value="recruitment">Recrutements ({categoryCounts.recruitment ?? 0})</option>
                    </select>

                    <div className="flex items-center rounded-lg border border-border overflow-hidden">
                        {(["all", "pending", "approved", "rejected"] as StatusFilter[]).map((v) => (
                            <button
                                key={v}
                                onClick={() => { setStatusFilter(v); setPage(1); }}
                                className={`text-xs px-2.5 py-1.5 transition-colors duration-150 ${
                                    statusFilter === v ? "bg-ink text-ink-inverse" : "text-ink-muted hover:bg-surface-sunken"
                                }`}
                            >
                                {v === "all" ? "Tous" : v === "pending" ? (
                                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />En attente</span>
                                ) : v === "approved" ? (
                                    <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5" />Approuvés</span>
                                ) : (
                                    <span className="flex items-center gap-1"><Ban className="w-3.5 h-3.5" />Rejetés</span>
                                )}
                            </button>
                        ))}
                    </div>

                    <select
                        value={franchiseFilter}
                        onChange={(e) => { setFranchiseFilter(e.target.value as "" | "true" | "false"); setPage(1); }}
                        className={selectCls}
                    >
                        <option value="">Franchise : toutes</option>
                        <option value="true">Franchise</option>
                        <option value="false">Non-franchise</option>
                    </select>

                    <select
                        value={franceFilter}
                        onChange={(e) => { setFranceFilter(e.target.value as "" | "true" | "false"); setPage(1); }}
                        className={selectCls}
                    >
                        <option value="">Pays : tous</option>
                        <option value="true">France</option>
                        <option value="false">Hors France</option>
                    </select>

                    <button
                        onClick={() => { setHasPhotoFilter((v) => v === "false" ? "" : "false"); setPage(1); }}
                        title="Articles en attente sans photo"
                        className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors duration-150 ${FOCUS_RING} ${
                            hasPhotoFilter === "false" ? "bg-ink text-ink-inverse border-ink" : "border-border text-ink-muted hover:bg-surface-sunken"
                        }`}
                    >
                        <ImageOff className="w-3.5 h-3.5" />
                        Sans photo{missingCounts.noPhoto > 0 && ` (${missingCounts.noPhoto})`}
                    </button>

                    <button
                        onClick={() => { setHasEstablishmentFilter((v) => v === "false" ? "" : "false"); setPage(1); }}
                        title="Articles en attente sans établissement lié"
                        className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors duration-150 ${FOCUS_RING} ${
                            hasEstablishmentFilter === "false" ? "bg-ink text-ink-inverse border-ink" : "border-border text-ink-muted hover:bg-surface-sunken"
                        }`}
                    >
                        <Unlink className="w-3.5 h-3.5" />
                        Sans établissement{missingCounts.noEstablishment > 0 && ` (${missingCounts.noEstablishment})`}
                    </button>

                    <button
                        onClick={() => { setPickableFilter((v) => !v); setPage(1); }}
                        title="Images scrapées disponibles, mais aucune image de preview choisie"
                        className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors duration-150 ${FOCUS_RING} ${
                            pickableFilter ? "bg-ink text-ink-inverse border-ink" : "border-border text-ink-muted hover:bg-surface-sunken"
                        }`}
                    >
                        <Images className="w-3.5 h-3.5" />
                        Image à choisir{missingCounts.pickable > 0 && ` (${missingCounts.pickable})`}
                    </button>

                    {hasActiveFilters && (
                        <button onClick={resetFilters}
                            className={`flex items-center gap-1 text-xs text-ink-muted hover:text-ink px-2 py-1.5 rounded-lg hover:bg-surface-sunken transition-colors ${FOCUS_RING}`}>
                            <X className="w-3.5 h-3.5" />Réinitialiser
                        </button>
                    )}

                    <div className="ml-auto flex items-center gap-2">
                        {hasActiveFilters && (
                            <span className="flex items-center gap-1 text-xs text-ink-muted">
                                <Filter className="w-3.5 h-3.5" />{total} résultat{total > 1 ? "s" : ""}
                            </span>
                        )}
                        <button
                            onClick={handleScrapeBatch}
                            disabled={scrapingBatch}
                            title={`Scraper les images des articles jamais scrapés${missingCounts.unscraped ? ` (${missingCounts.unscraped} restants)` : ""}`}
                            className={`flex items-center gap-1.5 text-xs font-medium border border-border-strong bg-surface hover:bg-surface-sunken px-3 py-1.5 rounded-lg disabled:opacity-50 transition-colors ${FOCUS_RING}`}
                        >
                            <Images className={`w-3.5 h-3.5 ${scrapingBatch ? "animate-pulse" : ""}`} />
                            {scrapingBatch ? "Scraping…" : `Scraper les images${missingCounts.unscraped ? ` (${missingCounts.unscraped})` : ""}`}
                        </button>
                        <button
                            onClick={handleFetch}
                            disabled={fetching}
                            className={`flex items-center gap-1.5 text-xs font-medium border border-border-strong bg-surface hover:bg-surface-sunken px-3 py-1.5 rounded-lg disabled:opacity-50 transition-colors ${FOCUS_RING}`}
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${fetching ? "animate-spin" : ""}`} />
                            {fetching ? "Import…" : "Importer"}
                        </button>
                    </div>
                </div>

                {/* Jev — quick triage lists, counted over the pending articles of the selected week */}
                <div className="flex flex-wrap items-center gap-2 mb-4 -mt-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-faint mr-1">Jev</span>
                    {JEV_QUICK_FILTERS.map(({ key, label, title, icon: Icon }) => (
                        <button
                            key={key}
                            onClick={() => { setJevQuick((v) => v === key ? "" : key); setPage(1); }}
                            title={title}
                            aria-pressed={jevQuick === key}
                            className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors duration-150 ${FOCUS_RING} ${
                                jevQuick === key ? "bg-ink text-ink-inverse border-ink" : "border-border text-ink-muted hover:bg-surface-sunken"
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" />
                            {label}
                            <span className={`font-mono tabular-nums ${jevQuick === key ? "" : "text-ink-faint"}`}>{jevCounts[key]}</span>
                        </button>
                    ))}

                    <div className="ml-auto flex items-center gap-2">
                        <span className="text-xs text-ink-muted">Tri</span>
                        <div className="flex items-center rounded-lg border border-border overflow-hidden">
                            {([["published", "Date"], ["jev", "Score Jev"]] as const).map(([v, label]) => (
                                <button
                                    key={v}
                                    onClick={() => { setSortBy(v); setPage(1); }}
                                    aria-pressed={sortBy === v}
                                    className={`text-xs px-2.5 py-1.5 transition-colors duration-150 ${FOCUS_RING} ${
                                        sortBy === v ? "bg-ink text-ink-inverse" : "text-ink-muted hover:bg-surface-sunken"
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Type selon Jev — one tab per event, counted over the pending articles of the selected week */}
                <div className="mb-4 flex items-center gap-1 overflow-x-auto border-b border-border" role="tablist" aria-label="Type selon Jev">
                    {([["", "Tous"], ...(Object.keys(JEV_EVENT_LABEL) as JevEvent[]).map((k) => [k, JEV_EVENT_LABEL[k]] as const)] as const).map(([key, label]) => {
                        // A quick chip that already pins the type ("Fermetures") wins over the tabs.
                        const active = jevQuick === "closings" ? key === "closing" : jevEvent === key;
                        const count = jevEventCounts[key === "" ? "all" : key] ?? 0;
                        return (
                            <button
                                key={key || "all"}
                                role="tab"
                                aria-selected={active}
                                disabled={jevQuick === "closings"}
                                onClick={() => { setJevEvent(key); setPage(1); }}
                                className={`-mb-px flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-xs transition-colors duration-150 disabled:cursor-not-allowed ${FOCUS_RING} ${
                                    active ? "border-ink font-semibold text-ink" : "border-transparent text-ink-muted hover:text-ink"
                                }`}
                            >
                                {label}
                                <span className={`font-mono tabular-nums ${active ? "text-ink-muted" : "text-ink-faint"}`}>({count})</span>
                            </button>
                        );
                    })}
                </div>

                {/* list */}
                <div className="rounded-2xl border border-border bg-surface p-2">
                    {loading ? (
                        <div className="space-y-1">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} className="flex gap-3 py-3 px-3 animate-pulse">
                                    <div className="w-14 h-10 rounded-md bg-surface-sunken flex-shrink-0" />
                                    <div className="flex-1 space-y-1.5 pt-0.5">
                                        <div className="h-2.5 bg-surface-sunken rounded-full w-1/4" />
                                        <div className="h-3 bg-surface-sunken rounded-full w-full" />
                                        <div className="h-3 bg-surface-sunken rounded-full w-3/4" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : items.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                            <p className="text-sm text-ink-muted">Aucun article{hasActiveFilters ? " pour ces filtres" : ""}</p>
                            {hasActiveFilters && (
                                <button onClick={resetFilters} className={`mt-3 text-xs text-ink-muted underline rounded ${FOCUS_RING}`}>
                                    Réinitialiser les filtres
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {items.map((item) => (
                                <ArticleRow
                                    key={item._id}
                                    item={item}
                                    allFranchises={allFranchises}
                                    onSetStatus={handleSetStatus}
                                    onSetCity={handleSetCity}
                                    onSetCategory={handleSetCategory}
                                    onSetImage={handleSetImage}
                                    onScrapeImages={handleScrapeImages}
                                    onSetEstablishment={handleSetEstablishment}
                                    onSetFranchise={handleSetFranchise}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {!loading && items.length > 0 && totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                            className={`text-xs font-medium text-ink-muted hover:text-ink disabled:opacity-30 transition-colors rounded ${FOCUS_RING}`}>
                            ← Précédent
                        </button>
                        <span className="text-xs text-ink-faint">Page {page} / {totalPages} · {total} articles</span>
                        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                            className={`text-xs font-medium text-ink-muted hover:text-ink disabled:opacity-30 transition-colors rounded ${FOCUS_RING}`}>
                            Suivant →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
